// src/components/ui/SegmentedTabs.jsx
//
// Tabs drawn as a segmented control (grey track, white pill for the active one) —
// the Brand Voice design, and the same look as billing's PlanAxisToggle. ⭐ Real tab
// semantics (role=tablist / tab, aria-selected, aria-controls) and ARROW-KEY
// movement, because these switch a panel, not a value.
// ⛔ Buttons, never links: a screen with one save bar and a leave guard must be able
// to switch tabs without "navigating".
// ⭐ v184: a tab may carry `dirty: true` (unsaved changes on its panel). The DOT is
// decoration — `aria-hidden`, because a colour is not a message — and the meaning
// rides in the accessible name instead, as the visually-hidden `dirtyLabel` the
// caller supplies. ⛔ Without a `dirtyLabel` the dot stays unannounced rather than
// inventing copy here: this component holds none.
// ⭐ v193: `variant="band"` draws the SAME tabs as the console's underlined tab row (the shared
// `PAGE_BAND_TAB` class of PageBand.jsx) — for a tab strip placed in a PageBand. Semantics, keyboard
// and the dirty dot are identical in both variants; only the look changes.
//
// Fully CONTROLLED: `aria-selected` comes from `value` and no selection is stored
// here, so a page's ?tab= mirror can never disagree with what is drawn. A roving
// tabIndex makes the strip ONE tab stop (Tab moves on to the panel); ArrowLeft /
// ArrowRight wrap and move selection AND focus.
// ⚠️ EVERY tab's aria-controls points at ONE id, `<idPrefix>-panel` — the design
// swaps one body rather than stacking panels — so the page MUST render exactly one
// element with that id and role="tabpanel", or the reference dangles.
// Holds no copy: the tablist label and the tab labels come from the caller.
import React, { useRef } from 'react';
import { COLORS, FONT_SANS, RADII } from '../../constants/theme.js';
import { PAGE_BAND_TAB } from './PageBand.jsx';

// Off-screen but IN the accessibility tree (⛔ never `display: none`, which would
// remove it from the accessible name too).
const SR_ONLY = {
  position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
  overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
};
const DOT = {
  display: 'inline-block', width: 8, height: 8, marginLeft: 6,
  borderRadius: RADII.pill, background: COLORS.accent, verticalAlign: 'middle',
};

export default function SegmentedTabs({ tabs, value, onChange, label, idPrefix = 'tab', dirtyLabel, variant = 'pill' }) {
  const band = variant === 'band';
  const refs = useRef({});
  const move = (delta) => {
    const i = tabs.findIndex((t) => t.key === value);
    const next = tabs[(i + delta + tabs.length) % tabs.length];
    if (!next) return;
    onChange(next.key);
    refs.current[next.key]?.focus();
  };
  return (
    <div role="tablist" aria-label={label} style={band
      ? { display: 'flex', flexWrap: 'wrap', gap: 4 }
      : { display: 'flex', flexWrap: 'wrap', gap: 4, padding: 4, background: COLORS.field, borderRadius: RADII.control }}>
      {tabs.map((t) => {
        const active = t.key === value;
        return (
          <button
            key={t.key}
            ref={(el) => { refs.current[t.key] = el; }}
            type="button" role="tab" id={`${idPrefix}-${t.key}`}
            aria-selected={active} aria-controls={`${idPrefix}-panel`} tabIndex={active ? 0 : -1}
            onClick={() => onChange(t.key)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight') { e.preventDefault(); move(1); }
              if (e.key === 'ArrowLeft') { e.preventDefault(); move(-1); }
            }}
            className={band ? PAGE_BAND_TAB : undefined}
            style={band ? undefined : {
              flex: '1 1 auto', padding: '10px 16px', border: 'none', borderRadius: RADII.controlInner,
              background: active ? COLORS.surface : 'transparent',
              color: active ? COLORS.ink : COLORS.textSecondary,
              fontSize: 13, fontWeight: active ? 600 : 500, fontFamily: FONT_SANS,
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            {t.label}
            {t.dirty && <span aria-hidden="true" style={DOT} />}
            {t.dirty && dirtyLabel && <span style={SR_ONLY}>{` — ${dirtyLabel}`}</span>}
          </button>
        );
      })}
    </div>
  );
}
